// src/backendServices/liveStreamManagment/local_media_store.ts
//
// Stockage local des médias de la régie via IndexedDB.
//
// slot.url            = objectURL temporaire (blob:http://...) — utilisable dans <img>/<video>/<audio>
// slot.instantMediaId = clé IndexedDB — permet de régénérer l'objectURL après rechargement
//
// À la sauvegarde REST  : preparePayloadForSave() vide slot.url ET slot.instantMediaId
//                         (les instantMediaId sont propres à la machine, inutiles côté serveur)
// Au chargement REST    : restorePayloadMediaUrls() supprimé — non pertinent cross-machine
//
// Nouveau : clearSlotsFromIndexedDB() — nettoie les blobs IndexedDB des slots actuels
//           avant d'installer une nouvelle configuration.
//
// Installation : npm i idb

import { openDB, IDBPDatabase } from 'idb';
import { isStreamedMediaUrl } from '../../dataTypes/liveStreamManagment/utils_videoSource';

// ── Config ────────────────────────────────────────────────────────────────────

const DB_NAME    = 'regie_media_store';
const DB_VERSION = 1;
const STORE_NAME = 'media_blobs';

// ── Types ─────────────────────────────────────────────────────────────────────

export type LocalMediaType = 'image' | 'sound' | 'video' | 'scene';

interface MediaRecord {
  id        : string;
  mimeType  : string;
  filename  : string;
  mediaType : LocalMediaType;
  slotId    : string;
  role      : string;
  storedAt  : number;
  blob      : Blob;
}

export interface StoreResult {
  /** À stocker dans slot.instantMediaId — remplace l'ancien si présent */
  mediaId   : string;
  /** À stocker dans slot.url */
  objectUrl : string;
}

// ── Singleton DB ──────────────────────────────────────────────────────────────

let _db: IDBPDatabase | null = null;

async function getDb(): Promise<IDBPDatabase> {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    },
  });
  return _db;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  avif: 'image/avif', svg: 'image/svg+xml', bmp: 'image/bmp',
  mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', mkv: 'video/x-matroska',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4', aac: 'audio/aac', flac: 'audio/flac',
};

/** Type MIME déduit de l'extension d'un nom de fichier, ou '' si inconnue. */
export function mimeFromName(name: string | undefined): string {
  const ext = name?.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXT[ext] ?? '';
}

/**
 * Un Blob rapporté par le serveur sans Content-Type reste sans type : l'objectURL
 * qui en découle se lit mal dans <audio>/<video>, et un envoi ultérieur le range
 * en « .bin ». On lui rend son type d'après le nom d'origine.
 */
function withKnownType(blob: Blob, filename: string): Blob {
  if (blob.type) return blob;
  const mime = mimeFromName(filename);
  return mime ? new Blob([blob], { type: mime }) : blob;
}

function generateMediaId(slotId: string, role: string): string {
  return `${slotId}__${role}__${Date.now()}__${Math.random().toString(36).slice(2, 7)}`;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime   = header.match(/:(.*?);/)?.[1] ?? 'application/octet-stream';
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// ── API publique ───────────────────────────────────────────────────────────────

/**
 * Stocke un Blob dans IndexedDB (depuis une data URL ou directement).
 *
 * Si le slot avait déjà un instantMediaId (previousMediaId), l'ancien Blob
 * est supprimé de IndexedDB avant d'écrire le nouveau — pas d'orphelins.
 *
 * Retourne { mediaId, objectUrl } à mettre dans le slot.
 */
export async function storeMediaDataUrl(
  dataUrl         : string,
  filename        : string,
  mediaType       : LocalMediaType,
  slotId          : string,
  role            : string = 'slot',
  previousMediaId : string | undefined = undefined,
): Promise<StoreResult> {
  const blob    = dataUrlToBlob(dataUrl);
  const mediaId = generateMediaId(slotId, role);

  const record: MediaRecord = {
    id: mediaId, mimeType: blob.type, filename,
    mediaType, slotId, role,
    storedAt: Date.now(), blob,
  };

  const db = await getDb();

  if (previousMediaId) {
    forgetRestoredUrls([previousMediaId]);
    try {
      await db.delete(STORE_NAME, previousMediaId);
    } catch {
      // Pas bloquant
    }
  }

  await db.put(STORE_NAME, record);

  return { mediaId, objectUrl: URL.createObjectURL(blob) };
}

/**
 * Comme storeMediaDataUrl, mais accepte aussi une url distante (vidéo HLS du
 * compte, choisie dans « Library ») : rien n'est rangé dans IndexedDB, l'url
 * devient telle quelle celle du slot et `mediaId` vaut undefined. L'ancien Blob
 * du slot est tout de même supprimé — le slot ne le désigne plus.
 */
export async function storeMediaSource(
  source          : string,
  filename        : string,
  mediaType       : LocalMediaType,
  slotId          : string,
  role            : string = 'slot',
  previousMediaId : string | undefined = undefined,
): Promise<{ mediaId: string | undefined; objectUrl: string }> {
  if (!isStreamedMediaUrl(source))
    return storeMediaDataUrl(source, filename, mediaType, slotId, role, previousMediaId);
  if (previousMediaId) await deleteMediaRecord(previousMediaId);
  return { mediaId: undefined, objectUrl: source };
}

/**
 * Stocke un Blob directement dans IndexedDB (depuis un fetch ou un ZIP).
 *
 * Équivalent de storeMediaDataUrl mais accepte un Blob au lieu d'une data URL.
 * Utilisé lors du chargement depuis serveur ou depuis ZIP.
 */
export async function storeMediaBlob(
  blob            : Blob,
  filename        : string,
  mediaType       : LocalMediaType,
  slotId          : string,
  role            : string = 'slot',
  previousMediaId : string | undefined = undefined,
): Promise<StoreResult> {
  blob = withKnownType(blob, filename);
  const mediaId = generateMediaId(slotId, role);

  const record: MediaRecord = {
    id: mediaId, mimeType: blob.type, filename,
    mediaType, slotId, role,
    storedAt: Date.now(), blob,
  };

  const db = await getDb();

  if (previousMediaId) {
    forgetRestoredUrls([previousMediaId]);
    try {
      await db.delete(STORE_NAME, previousMediaId);
    } catch {
      // Pas bloquant
    }
  }

  await db.put(STORE_NAME, record);

  return { mediaId, objectUrl: URL.createObjectURL(blob) };
}

/**
 * Adresses `blob:` déjà créées par restoreMediaUrl, une par média.
 *
 * Chaque URL.createObjectURL retient son Blob en mémoire jusqu'à
 * revokeObjectURL — que rien n'appelait : chaque réhydratation des slots (à
 * chaque changement de chaîne) en créait de nouvelles pour les mêmes médias,
 * et les précédentes ne se libéraient jamais. On rend désormais la même
 * adresse pour le même média, et on la libère quand il est supprimé ou
 * remplacé (forgetRestoredUrls).
 */
const restoredUrls = new Map<string, string>();

function forgetRestoredUrls(mediaIds: Array<string | undefined>): void {
  for (const id of mediaIds) {
    if (!id) continue;
    const url = restoredUrls.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      restoredUrls.delete(id);
    }
  }
}

/**
 * Récupère un Blob depuis IndexedDB et génère un objectURL — toujours le
 * même pour un même média (cf. restoredUrls).
 * Retourne null si absent (autre machine, autre navigateur, nettoyage navigateur).
 */
export async function restoreMediaUrl(mediaId: string): Promise<string | null> {
  try {
    const db     = await getDb();
    const record = await db.get(STORE_NAME, mediaId) as MediaRecord | undefined;
    if (!record?.blob) {
      forgetRestoredUrls([mediaId]);
      return null;
    }
    const known = restoredUrls.get(mediaId);
    if (known) return known;
    const url = URL.createObjectURL(record.blob);
    restoredUrls.set(mediaId, url);
    return url;
  } catch {
    return null;
  }
}

/**
 * Le média est-il dans IndexedDB ? Sans créer d'adresse `blob:` — pour qui
 * veut seulement savoir s'il est déjà là.
 */
export async function hasMediaRecord(mediaId: string): Promise<boolean> {
  try {
    const db = await getDb();
    return (await db.getKey(STORE_NAME, mediaId)) !== undefined;
  } catch {
    return false;
  }
}

/**
 * Supprime un Blob de IndexedDB.
 * À appeler depuis DeleteSlotButton quand slot.instantMediaId est renseigné.
 * Ne lève pas d'erreur si absent.
 */
export async function deleteMediaRecord(mediaId: string): Promise<void> {
  forgetRestoredUrls([mediaId]);
  try {
    const db = await getDb();
    await db.delete(STORE_NAME, mediaId);
  } catch (e) {
    console.warn(`[LocalMediaStore] Impossible de supprimer "${mediaId}" :`, e);
  }
}

/**
 * Supprime de IndexedDB tous les blobs référencés par une liste de slots simples.
 * À appeler avant d'écraser une feature (images, sons, vidéos) avec une nouvelle config.
 *
 * @param slots  Liste de slots ayant éventuellement un champ instantMediaId
 */
export async function clearSlotsFromIndexedDB(
  slots: Array<{ instantMediaId?: string }>,
): Promise<void> {
  const ids = slots.map(s => s.instantMediaId).filter((id): id is string => !!id);
  if (ids.length === 0) return;
  forgetRestoredUrls(ids);
  const db = await getDb();
  await Promise.all(ids.map(id => db.delete(STORE_NAME, id).catch(() => {})));
}

/**
 * Supprime de IndexedDB tous les blobs référencés par une liste de LayoutSlots
 * (background + incrustations).
 */
export async function clearSceneSlotsFromIndexedDB(
  slots: Array<{
    background    : { instantMediaId?: string };
    incrustations : Array<{ instantMediaId?: string }>;
  }>,
): Promise<void> {
  const ids: string[] = [];
  for (const slot of slots) {
    if (slot.background?.instantMediaId) ids.push(slot.background.instantMediaId);
    for (const inc of slot.incrustations ?? []) {
      if (inc.instantMediaId) ids.push(inc.instantMediaId);
    }
  }
  if (ids.length === 0) return;
  forgetRestoredUrls(ids);
  const db = await getDb();
  await Promise.all(ids.map(id => db.delete(STORE_NAME, id).catch(() => {})));
}

/**
 * Prépare un payload pour la sauvegarde REST :
 * - Vide slot.url (objectURL non persistable côté serveur)
 * - Vide slot.instantMediaId (clé IndexedDB propre à la machine, inutile cross-machine)
 *
 * Remplace stripMediaUrls() dans handleSave() et handleUploadLoad().
 */
export function preparePayloadForSave(payload: any, keepHasMedia = false): any {
  const result = JSON.parse(JSON.stringify(payload));

  // keepHasMedia : payload venu d'un FICHIER (JSON/ZIP) dont les url sont déjà vides
  // mais dont les médias existent — recalculer `!!url` effacerait leur trace.
  const has = (o: any) => keepHasMedia ? (!!o.url || !!o.hasMedia) : !!o.url;

  // Une url distante (vidéo HLS du compte) se garde telle quelle : elle vaut sur
  // n'importe quel poste, et aucun fichier ne l'accompagne (hasMedia false).
  const remote = (o: any) => isStreamedMediaUrl(o.url);
  const strip  = (o: any) => ({
    hasMedia      : remote(o) ? false : has(o),
    url           : remote(o) ? o.url : '',
    instantMediaId: undefined,
  });

  const stripSlot = (s: any) => ({ ...s, ...strip(s) });

  const stripUrls = (slots: any[]) => slots.map(stripSlot);

  const stripSceneUrls = (slots: any[]) =>
    slots.map((s: any) => ({
      ...s,
      background   : { ...s.background, ...strip(s.background) },
      incrustations: s.incrustations.map((ic: any) => ({ ...ic, ...strip(ic) })),
    }));

  if (result.data?.image?.slots) result.data.image.slots = stripUrls(result.data.image.slots);
  if (result.data?.son?.slots)   result.data.son.slots   = stripUrls(result.data.son.slots);
  if (result.data?.video?.slots) result.data.video.slots = stripUrls(result.data.video.slots);
  if (result.data?.scene?.slots) result.data.scene.slots = stripSceneUrls(result.data.scene.slots);

  return result;
}